import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';

const CustomerRatings = ({ onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [message, setMessage] = useState('');
  const [existingRating, setExistingRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { vendor_id, product_detail_id } = useParams();
  const { vendor_name, appointment_id } = location.state || {};

  // Check for existing rating when component loads
  useEffect(() => {
    const checkExistingRating = async () => {
      if (!appointment_id) {
        setLoading(false);
        return;
      }

      try {
  const res = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/customer_ratings/appointment/${appointment_id}`);
        const data = await res.json();
        
        if (res.ok && data.rating) {
          setExistingRating(data.rating);
          setRating(data.rating.rating);
          setComment(data.rating.comment || '');
          setMessage('You have already rated this appointment. You can update your rating below.');
        }
      } catch (err) {
        console.error('Error checking existing rating:', err);
      }
      setLoading(false);
    };

    checkExistingRating();
  }, [appointment_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      setMessage('Please select a rating.');
      return;
    }

    // Get user_id from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const user_id = user.id;

    if (!user_id || !vendor_id) {
      setMessage('Missing required information.');
      return;
    }

    // Submit rating and comment to backend
    try {
      const method = existingRating ? 'PUT' : 'POST';
      const url = existingRating 
  ? `${process.env.REACT_APP_VENDOR_DOMAIN}/customer_ratings/${existingRating.id}`
  : `${process.env.REACT_APP_VENDOR_DOMAIN}/customer_ratings`;
        
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          rating, 
          comment, 
          user_id,
          vendor_id,
          product_detail_id,
          appointment_id: appointment_id || null,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(existingRating ? 'Rating updated successfully!' : 'Thank you for your feedback!');
        if (!existingRating) {
          setExistingRating({ id: data.ratingId, rating, comment });
        }
        setIsEditing(false);
      } else {
        setMessage(data.message || 'Error submitting rating.');
      }
    } catch {
      setMessage('Error submitting rating.');
    }
    if (onSubmit) onSubmit({ rating, comment });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>
        {existingRating && !isEditing ? 'Your Rating' : 'Rate Your Experience'}
      </h2>
      {vendor_name && <p style={styles.vendorName}>Rating for: {vendor_name}</p>}
      
      {existingRating && !isEditing ? (
        // Display existing rating
        <div style={styles.existingRating}>
          <div style={styles.stars}>
            {[1, 2, 3, 4, 5].map(star => (
              <span
                key={star}
                style={{
                  fontSize: '2rem',
                  color: rating >= star ? '#FFD700' : '#ccc',
                  marginRight: '6px',
                }}
              >
                ★
              </span>
            ))}
          </div>
          <div style={styles.existingComment}>
            <strong>Your comment:</strong><br />
            {comment || 'No comment provided'}
          </div>
          <button
            style={styles.editBtn}
            onClick={() => setIsEditing(true)}
          >
            Edit Rating
          </button>
        </div>
      ) : (
        // Show rating form
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.stars}>
            {[1, 2, 3, 4, 5].map(star => (
              <span
                key={star}
                style={{
                  fontSize: '2rem',
                  color: (hover || rating) >= star ? '#FFD700' : '#ccc',
                  cursor: 'pointer',
                  marginRight: '6px',
                }}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHover(star)}
                onMouseLeave={() => setHover(0)}
                role="button"
                aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
              >
                ★
              </span>
            ))}
          </div>
          <textarea
            style={styles.textarea}
            placeholder="Leave a comment..."
            value={comment}
            onChange={e => setComment(e.target.value)}
            rows={4}
          />
          <div style={styles.buttonRow}>
            <button type="submit" style={styles.button}>
              {existingRating ? 'Update Rating' : 'Submit Rating'}
            </button>
            {existingRating && isEditing && (
              <button
                type="button"
                style={styles.cancelBtn}
                onClick={() => {
                  setIsEditing(false);
                  setRating(existingRating.rating);
                  setComment(existingRating.comment || '');
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}
      
      {message && <div style={styles.message}>{message}</div>}
      
      <button
        style={styles.backBtn}
        onClick={() => navigate('/appointments')}
      >
        ← Back to Appointments
      </button>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '400px',
    margin: '40px auto',
    padding: '24px',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  title: {
    textAlign: 'center',
    marginBottom: '18px',
    color: '#8d6e36',
    fontSize: '1.3rem',
    fontWeight: 'bold',
  },
  vendorName: {
    textAlign: 'center',
    marginBottom: '16px',
    color: '#555',
    fontSize: '1rem',
    fontStyle: 'italic',
  },
  loading: {
    textAlign: 'center',
    color: '#8d6e36',
    fontSize: '1.1rem',
    padding: '20px',
  },
  existingRating: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  existingComment: {
    background: '#f9f9f9',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '16px',
    textAlign: 'left',
    border: '1px solid #eee',
  },
  editBtn: {
    background: '#4078c0',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontWeight: 'bold',
    fontSize: '1rem',
    cursor: 'pointer',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  stars: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '8px',
  },
  textarea: {
    borderRadius: '6px',
    border: '1px solid #ccc',
    padding: '10px',
    fontSize: '1rem',
    resize: 'vertical',
  },
  buttonRow: {
    display: 'flex',
    gap: '10px',
  },
  button: {
    background: '#8d6e36',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '12px',
    fontWeight: 'bold',
    fontSize: '1rem',
    cursor: 'pointer',
    flex: 1,
  },
  cancelBtn: {
    background: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '12px',
    fontWeight: 'bold',
    fontSize: '1rem',
    cursor: 'pointer',
    flex: 1,
  },
  message: {
    marginTop: '12px',
    textAlign: 'center',
    color: '#388e3c',
    fontWeight: 'bold',
  },
  backBtn: {
    background: '#4078c0',
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '6px',
    padding: '12px 24px',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px #e0cfa0',
    transition: 'background 0.2s',
    textDecoration: 'none',
    marginTop: '24px',
    display: 'block',
    width: '100%',
    textAlign: 'center',
  },
};

export default CustomerRatings;